// Ghost slot clips — the timeline's loops, offered in the Session View.
//
// Most Sets that reach here were built entirely in the Arrangement: their
// Session View is empty, so the player's grid has nothing to hit and the whole
// screen collapses to a timeline you can only watch. But the material is right
// there on that timeline — in a typical Set the same handful of 2-, 4- and
// 8-bar loops placed over and over — and those are exactly the things a person
// handed the player wants to launch.
//
// So they are mirrored into slots: one per distinct piece of material, on its
// own track's column, in rows BELOW whatever real scenes the Set has. They are
// GHOSTS, marked as such and drawn as such, because they are inferred and Live
// knows nothing about them: the producer never put a clip in that slot, and a
// grid that hid the difference would be inventing a Session View that doesn't
// exist. Real scenes always come first and are never touched.
//
// What qualifies is everything the track can actually sound. That is the same
// de-duplicated set of pads the room used to build straight off the timeline,
// with one difference that matters: the pieces are keyed by MATERIAL rather
// than by name, so forty placements of one kick are one pad rather than forty.
// Nothing is held back for being an awkward length — a 3.4-bar slice of an
// arrangement is still a thing you might want to hit. What its length decides
// is whether hitting it LOOPS or plays it once (`loopsOnLaunch`,
// lib/session-launch.ts); the room used to paper over ragged lengths by
// snapping every pad's loop to the nearest bar, which silently played
// something the producer never wrote.
//
// Pure: it takes an arrangement and returns one. Nothing here is room state
// and nothing here is ever proposed — the editor's arrangement stays the
// un-ghosted one, and only the player's copy grows these rows.

import type { Meter } from "@web-shared/lib/meter";
import { isLooped } from "@web-shared/lib/clip-loop";
import { launchableSlotClip } from "@web-shared/lib/session-launch";
import { variantKey } from "@web-shared/lib/material";
import { notesDigest } from "@shared/midi/notes.js";
import type { Arrangement, ClipGeometry, Lane, LaneClip } from "@web-shared/lib/arrangement";

/**
 * What makes two placements the same ghost — `lib/material.ts`, the one rule
 * the shelf groups its cards by, read off the lane model. Never the object:
 * Live's clip-edge fades give the same four bars a different render hash
 * depending on what it sits next to, which showed up as three pads for one
 * loop.
 */
function materialKey(clip: LaneClip, variantOf: VariantOf): string {
  const g = clip.geometry;
  return variantKey(
    {
      kind: clip.kind,
      name: clip.name,
      duration: clip.duration,
      startMarker: g?.startMarker,
      endMarker: g?.endMarker,
      looping: g?.looping,
      warped: g?.isWarped,
      warpMode: g?.warpMode,
      ...(g?.transpose ? { transpose: g.transpose } : {}),
      ...(g?.detune ? { detune: g.detune } : {}),
      ...(g?.warpSettings ? { warpSettings: g.warpSettings } : {}),
      notesDigest: clip.kind === "midi" ? notesDigest(clip.notes ?? []) : undefined,
    },
    clip.audioKey ? variantOf(clip.audioKey) : undefined,
  );
}

/** A fingerprint of an object's actual audio, where one has been resolved. */
type VariantOf = (audioKey: string) => string | undefined;
const NO_VARIANTS: VariantOf = () => undefined;

/**
 * A ghost's loop brace.
 *
 * A launched slot clip with no brace loops its whole object (audio-engine).
 * For an unwarped clip that is exactly right — its object IS the region render
 * of the clip. A WARPED clip's object is the whole source file, so without a
 * brace a launch would loop the rest of the file instead of the two bars on
 * screen; brace it over the clip's own markers. A clip Live already braced is
 * left alone: its loop is the producer's, not ours to restate.
 */
function ghostGeometry(g: ClipGeometry, duration: number): ClipGeometry {
  if (!g.isWarped || isLooped(g)) return g;
  const start = Math.max(0, g.startMarker);
  return {
    ...g,
    looping: true,
    loopStart: start,
    loopEnd: g.endMarker > start ? g.endMarker : start + duration,
  };
}

function ghostOf(clip: LaneClip, slot: number): LaneClip {
  return {
    ...clip,
    // Its own id: a launched ghost is a launched ghost, and the lane's
    // "what is playing" must never read as the timeline clip it came from.
    id: `ghost:${clip.id}`,
    // A slot clip has no timeline position — the same invariant `fromRoom`
    // keeps when it parts slot clips from timeline ones.
    start: 0,
    slot,
    ghost: true,
    ...(clip.geometry ? { geometry: ghostGeometry(clip.geometry, clip.duration) } : {}),
  };
}

/** The lane's ghosts, in first-placement order (`lane.clips` is by start). */
function ghostsFor(lane: Lane, firstRow: number, variantOf: VariantOf): LaneClip[] {
  const ghosts: LaneClip[] = [];
  // The lane's REAL slots count as already seen: a loop the producer put in
  // the Session View and also placed on the timeline is one pad, in the row
  // they chose, not that row plus a ghost of itself underneath.
  const seen = new Set(lane.sessionClips.map((c) => materialKey(c, variantOf)));
  for (const clip of lane.clips) {
    // Nothing to sound, nothing to offer: a slot never plays its lane's stem,
    // so a keyless audio clip would be a pad that lies (`launchableSlotClip`
    // is the same rule the grid draws inert by). That is the ONLY bar to
    // clear — length decides how a pad plays, not whether it exists.
    if (!launchableSlotClip(clip)) continue;
    const key = materialKey(clip, variantOf);
    if (seen.has(key)) continue;
    seen.add(key);
    ghosts.push(ghostOf(clip, firstRow + ghosts.length));
  }
  return ghosts;
}

/**
 * Where the ghost rows begin, and why there are two answers.
 *
 * `afterOccupied` (the default) is one past the last slot a clip actually sits
 * in, NOT past the last declared scene. A Set carries every scene it has ever
 * had — eight of them with one clip in the first is ordinary — and a surface
 * that draws its rows as ONE numbered list would bury the ghosts under seven
 * empty rows nobody scrolls to. A scene row Live declares but never filled
 * does nothing when launched, so filling it costs nothing.
 *
 * `afterScenes` starts past every DECLARED scene instead. A surface that draws
 * the two kinds of row as separate bands (the room's Pads screen, via
 * `splitBands`) does not need that trick, and pays for it: with ghosts sitting
 * in rows 1 and 2, the Set's own scenes read "1, 4, 5, 6, 7, 8". Kept apart,
 * every scene keeps Live's number.
 */
export interface GhostOptions {
  meter?: Meter;
  startRow?: "afterOccupied" | "afterScenes";
  /** Tells two objects apart that describe themselves alike — resolved only
   * for those that collide (hooks/useMaterialVariants.ts). Absent ⇒ the
   * description stands alone, which is right for the common case. */
  variantOf?: (audioKey: string) => string | undefined;
}

function firstGhostRow(arrangement: Arrangement, startRow: "afterOccupied" | "afterScenes"): number {
  const last = arrangement.lanes.reduce(
    (max, lane) => lane.sessionClips.reduce((m, c) => Math.max(m, c.slot ?? -1), max),
    -1,
  );
  return startRow === "afterScenes" ? Math.max(last + 1, arrangement.sceneCount) : last + 1;
}

/**
 * The same arrangement with ghost slot clips appended — identity-equal when
 * there are none, so a Set with a real Session View and nothing on its
 * timeline to offer is left exactly as it was.
 */
export function withGhostSlots(arrangement: Arrangement, options: GhostOptions = {}): Arrangement {
  const { startRow = "afterOccupied", variantOf = NO_VARIANTS } = options;
  const firstRow = firstGhostRow(arrangement, startRow);
  const byLane = arrangement.lanes.map((lane) => ghostsFor(lane, firstRow, variantOf));
  const rows = byLane.reduce((max, ghosts) => Math.max(max, ghosts.length), 0);
  if (rows === 0) return arrangement;

  // The Set's own scenes are never dropped: a declared scene past the ghosts
  // stays a row, empty, exactly as Live has it.
  const sceneCount = Math.max(arrangement.sceneCount, firstRow + rows);
  return {
    ...arrangement,
    lanes: arrangement.lanes.map((lane, i) =>
      byLane[i]!.length === 0 ? lane : { ...lane, sessionClips: [...lane.sessionClips, ...byLane[i]!] },
    ),
    sceneCount,
    // Ghost rows are unnamed: Live never named them, and the grid numbers
    // them like any other scene row it has no name for.
    sceneNames: Array.from({ length: sceneCount }, (_, i) => arrangement.sceneNames[i] ?? ""),
  };
}
